/**
 * Liste des camions à côté de la carte: un clic centre la carte sur le camion
 * et ouvre sa fenêtre. Recherche, filtres (état, origine) et tri inclus.
 *
 * Les règles (signal, mouvement, âge, libellés de source) viennent toutes de
 * `components/map/fleetRules.js` : la liste et la carte disent la même chose.
 *
 * La liste se lit par pages (20, 50 ou 100, clé d'adresse `flotte`): c'est la
 * liste seule qui est découpée, la carte reçoit toujours tous les camions.
 * Un camion choisi sur la carte fait tourner la liste jusqu'à sa page.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Truck, Search, Phone, ArrowUpDown } from 'lucide-react';

import { libelle } from '../lib/libelles';
import { enDate } from '../lib/formats';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from './ui/pagination-bar';
import SignalBadge from './map/SignalBadge';
import { arretInquietant, couleurFamille, familleDuCamion, libelleFamille, FAMILLES } from './map/status';
import {
  ORIGINES,
  TEXTE_CONFLIT,
  TEXTE_SECOURS,
  ageTexte,
  arretDepuisSecondes,
  cleVehicule,
  dureeArretTexte,
  estEnMouvement,
  estPositionDeSecours,
  estPositionFraiche,
  libelleSource,
  origineDe,
  vitesseKmh,
  vitesseTexte,
} from './map/fleetRules';

const EMPTY_ARRAY = [];

const SORT_OPTIONS = [
  { key: 'matricule', label: 'Plaque' },
  { key: 'online', label: 'Signal' },
  { key: 'speed', label: 'Vitesse' },
  { key: 'arret', label: 'Durée d’arrêt' },
  { key: 'updated', label: 'Dernière position' },
];

const FAMILLES_FILTRABLES = FAMILLES.filter((famille) => famille !== 'unknown');

export default function FleetList({
  vehicles = EMPTY_ARRAY,
  selectedKey = null,
  onSelect,
  now = Date.now(),
  onlineOnly = false,
  onToggleOnlineOnly,
  /** Un filtre propre à l'écran (ex. la société), posé avant les filtres communs. */
  toolbar = null,
  /**
   * `panel` retire le cadre de carte et laisse la liste remplir son conteneur.
   */
  variant = 'card',
}) {
  const isPanel = variant === 'panel';
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState('online');
  const [sortDir, setSortDir] = useState('desc');
  const [filtreEtat, setFiltreEtat] = useState('all');
  const [filtreOrigine, setFiltreOrigine] = useState('all');

  const filtered = useMemo(() => {
    let rows = vehicles.slice();
    if (onlineOnly) rows = rows.filter((r) => estPositionFraiche(r, now));
    if (filtreEtat !== 'all') rows = rows.filter((r) => familleDuCamion(r) === filtreEtat);
    if (filtreOrigine !== 'all') rows = rows.filter((r) => origineDe(r) === filtreOrigine);
    if (q) {
      const lc = q.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.truck?.matricule || '').toLowerCase().includes(lc) ||
          (r.driver?.full_name || r.driver?.name || '').toLowerCase().includes(lc) ||
          (r.driver?.phone || '').toLowerCase().includes(lc) ||
          (r.trip_status ? libelle('tripStatus', r.trip_status, '') : '').toLowerCase().includes(lc),
      );
    }
    const getVal = (r) => {
      switch (sortKey) {
        case 'matricule':
          return (r.truck?.matricule || '').toLowerCase();
        case 'online':
          return estPositionFraiche(r, now) ? 1 : 0;
        case 'speed':
          return vitesseKmh(r) ?? -1;
        case 'updated':
          return enDate(r.timestamp)?.getTime() ?? 0;
        case 'arret':
          return arretDepuisSecondes(r, now) ?? -1;
        default:
          return '';
      }
    };
    rows.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [vehicles, q, onlineOnly, filtreEtat, filtreOrigine, sortKey, sortDir, now]);

  const pagination = usePagination(filtered, {
    cle: 'flotte',
    reinitialiser: [q, onlineOnly, filtreEtat, filtreOrigine, sortKey, sortDir].join('|'),
  });
  const { page, taille, allerA } = pagination;

  // Un camion choisi sur la carte (ou par un lien) peut se trouver sur une autre
  // page: la liste tourne jusqu'à lui. Elle ne le fait qu'au changement de choix,
  // pas quand l'utilisateur feuillette ensuite.
  const dernierChoix = useRef(null);
  useEffect(() => {
    if (selectedKey == null || dernierChoix.current === selectedKey) return;
    dernierChoix.current = selectedKey;
    const index = filtered.findIndex((v) => cleVehicule(v) === selectedKey);
    if (index < 0) return;
    const pageDuCamion = Math.floor(index / taille) + 1;
    if (pageDuCamion !== page) allerA(pageDuCamion);
  }, [selectedKey, filtered, taille, page, allerA]);

  // Une nouvelle page se lit depuis son premier camion.
  const defilement = useRef(null);
  useEffect(() => {
    defilement.current?.scrollTo?.(0, 0);
  }, [page, taille]);

  const choisir = (v) => onSelect?.(cleVehicule(v));

  return (
    <div
      className={
        isPanel
          ? 'flex h-full min-h-0 flex-col'
          : 'bg-card rounded-xl border shadow-sm'
      }
    >
      <div className="space-y-2 border-b p-3">
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/70"
            aria-hidden="true"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une plaque, un chauffeur…"
            aria-label="Rechercher un camion"
            className="w-full pl-7 pr-3 py-1.5 border rounded text-sm"
          />
        </div>
        {toolbar}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filtreEtat}
            onChange={(e) => setFiltreEtat(e.target.value)}
            aria-label="État du camion"
            className="min-w-0 flex-1 rounded border px-2 py-1.5 text-sm"
          >
            <option value="all">Tous les états</option>
            {FAMILLES_FILTRABLES.map((famille) => (
              <option key={famille} value={famille}>
                {libelleFamille(famille)}
              </option>
            ))}
          </select>
          <select
            value={filtreOrigine}
            onChange={(e) => setFiltreOrigine(e.target.value)}
            aria-label="Origine de la position"
            className="min-w-0 flex-1 rounded border px-2 py-1.5 text-sm"
          >
            <option value="all">Toutes les origines</option>
            {Object.entries(ORIGINES).map(([cle, texte]) => (
              <option key={cle} value={cle}>
                {texte}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="flex cursor-pointer items-center gap-1 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={onlineOnly}
              onChange={(e) => onToggleOnlineOnly?.(e.target.checked)}
            />
            Signal actif uniquement
          </label>
          <div className="flex items-center gap-1">
            <ArrowUpDown size={12} className="text-muted-foreground/70" aria-hidden="true" />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label="Trier par"
              className="rounded border px-2 py-1.5 text-sm"
            >
              {SORT_OPTIONS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
              className="rounded border bg-card px-2 py-1.5 text-xs"
              title="Inverser le tri"
              aria-label="Inverser le tri"
            >
              {sortDir === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      <div
        ref={defilement}
        className={
          isPanel ? 'min-h-0 flex-1 overflow-y-auto' : 'max-h-[320px] overflow-y-auto'
        }
      >
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground/70">
            Aucun camion à afficher
          </div>
        ) : (
          <ul className="divide-y">
            {pagination.lignes.map((v) => {
              const cle = cleVehicule(v);
              const isSelected = cle != null && cle === selectedKey;
              const signal = estPositionFraiche(v, now);
              const famille = familleDuCamion(v);
              const source = libelleSource(v.source);
              const age = ageTexte(v, now);
              const roule = estEnMouvement(v, now);
              const aUnTrajet = !!(v.trip_id || v.trip_status);
              const arret = dureeArretTexte(arretDepuisSecondes(v, now));
              return (
                <li
                  key={cle || `${v.trip_id}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={() => choisir(v)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      choisir(v);
                    }
                  }}
                  className={`flex cursor-pointer items-center gap-3 p-3 transition hover:bg-info/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                    isSelected ? 'border-l-4 border-info bg-info/10' : ''
                  }`}
                >
                  <div
                    className="flex size-9 shrink-0 items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: couleurFamille(famille) }}
                    title={libelleFamille(famille)}
                  >
                    <Truck size={16} aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">
                        {v.truck?.matricule || 'Camion sans plaque'}
                      </span>
                      <SignalBadge actif={signal} className="!px-1.5 !text-[10px]" />
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {aUnTrajet
                        ? `${v.driver?.full_name || v.driver?.name || 'Sans chauffeur'} · ${libelle('tripStatus', v.trip_status, 'Trajet en cours')}`
                        : `${libelleFamille(famille)} · Aucun trajet`}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                      {source && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                          {source}
                        </span>
                      )}
                      {arret && (
                        <span
                          className={
                            arretInquietant(v, now)
                              ? 'rounded bg-flame/15 px-1.5 py-0.5 font-medium text-flame-ink'
                              : 'rounded bg-muted px-1.5 py-0.5 text-muted-foreground'
                          }
                        >
                          À l’arrêt depuis {arret}
                        </span>
                      )}
                      {v.tracking_conflict && (
                        <span className="rounded bg-flame/15 px-1.5 py-0.5 text-flame-ink">
                          {TEXTE_CONFLIT}
                        </span>
                      )}
                      {estPositionDeSecours(v) && (
                        <span className="rounded bg-flame/15 px-1.5 py-0.5 text-flame-ink">
                          {TEXTE_SECOURS}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-xs text-muted-foreground">
                    <div className="font-medium text-foreground">
                      {vitesseTexte(v)}
                      {roule && <span className="sr-only"> (en mouvement)</span>}
                    </div>
                    <div>{age || '—'}</div>
                    {v.driver?.phone && (
                      <a
                        href={`tel:${v.driver.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-end gap-1 text-info"
                      >
                        <Phone size={10} aria-hidden="true" />
                        {v.driver.phone}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <BarrePagination pagination={pagination} nom="camions" className="m-2 mt-0 shrink-0" />
    </div>
  );
}
